import { Checkbox, FormControlLabel, IconButton, Popover, Stack, Tooltip } from '@mui/material'
import ViewColumnIcon from '@mui/icons-material/ViewColumn'
import { Table } from '@tanstack/react-table'
import React, { useMemo, useState } from 'react'
import { useAppTranslations } from '../../utils/hooks/useAppTranslations'
import { DataTableColumnMeta } from './DataTable.types'

interface DataTableColumnManagerProps<TData> {
  table: Table<TData>
}

const DataTableColumnManager = <TData,>({ table }: DataTableColumnManagerProps<TData>): React.ReactElement => {
  const t = useAppTranslations()
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)

  const columns = useMemo(
    () =>
      table.getAllLeafColumns().filter((column) => {
        const meta = column.columnDef.meta as DataTableColumnMeta<TData> | undefined
        return column.id !== 'actions' && meta?.hideable !== false
      }),
    [table]
  )

  return (
    <>
      <Tooltip title={t('table.columns')}>
        <IconButton
          onClick={(event) => setAnchorEl(event.currentTarget)}
          size="small"
          sx={{ ml: (theme) => theme.spacing(1) }}
        >
          <ViewColumnIcon />
        </IconButton>
      </Tooltip>
      <Popover
        anchorEl={anchorEl}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        onClose={() => setAnchorEl(null)}
        open={Boolean(anchorEl)}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
      >
        <Stack sx={{ minWidth: 220, p: 1 }}>
          {columns.map((column) => (
            <FormControlLabel
              control={
                <Checkbox checked={column.getIsVisible()} onChange={column.getToggleVisibilityHandler()} size="small" />
              }
              key={column.id}
              label={String(
                (column.columnDef.meta as DataTableColumnMeta<TData> | undefined)?.columnLabel ?? column.id
              )}
            />
          ))}
        </Stack>
      </Popover>
    </>
  )
}

export default DataTableColumnManager
